import React, { useState, useEffect, useMemo } from 'react'
import ContentMedia from './content-media'
import type { ContentInfoChanges } from '../types'

interface ContentMediaSectionProps {
  contentData: {
    id?: string
    previewMediaId?: string | null
    featureMediaId?: string | null
  } | null
  isViewOnly: boolean
  onDirtyChange: (isDirty: boolean) => void
  onChangesUpdate: (changes: ContentInfoChanges | null) => void
  onResetTriggered?: number
}

/**
 * Content Media Section Component
 * Displays and allows editing of preview and feature media for content detail
 * This component manages its own media state and reports dirty state and changes to parent
 * Following the same pattern as CourseContentInformationSection
 */
function ContentMediaSection ({
  contentData,
  isViewOnly,
  onDirtyChange,
  onChangesUpdate,
  onResetTriggered,
}: ContentMediaSectionProps) {
  const id = contentData?.id

  // Local state for media fields
  const [mediaValues, setMediaValues] = useState({
    previewMediaId: null as string | null,
    featureMediaId: null as string | null,
  })

  // Track initial values to detect changes
  const [initialValues, setInitialValues] = useState<{
    previewMediaId: string | null
    featureMediaId: string | null
  }>({
    previewMediaId: null,
    featureMediaId: null,
  })

  // Update media values when content data loads
  useEffect(() => {
    if (contentData) {
      const values = {
        previewMediaId: contentData.previewMediaId || null,
        featureMediaId: contentData.featureMediaId || null,
      }
      setMediaValues(values)
      setInitialValues(values)
    }
  }, [contentData])

  // Track if media has unsaved changes
  const isDirty = useMemo(() => {
    // If no initial values loaded yet, not dirty
    if (!initialValues || (!initialValues.previewMediaId && !initialValues.featureMediaId)) {
      // Only check if we have initial values set (after first load)
      if (Object.keys(initialValues).length === 0) {
        return false
      }
    }

    // Compare current media values with initial values
    const previewChanged = mediaValues.previewMediaId !== initialValues.previewMediaId
    const featureChanged = mediaValues.featureMediaId !== initialValues.featureMediaId

    return previewChanged || featureChanged
  }, [mediaValues, initialValues])

  // Notify parent when dirty state changes
  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  // Build changed values object
  const changedValues = useMemo(() => {
    if (!initialValues || Object.keys(initialValues).length === 0) {
      return null
    }

    const changes: Record<string, unknown> = { id }
    let hasChanges = false

    // Check if previewMediaId changed
    if (mediaValues.previewMediaId !== initialValues.previewMediaId) {
      hasChanges = true
      // Convert to number or null for GraphQL mutation
      changes.previewMediaId = mediaValues.previewMediaId
        ? Number(mediaValues.previewMediaId)
        : null
    }

    // Check if featureMediaId changed
    if (mediaValues.featureMediaId !== initialValues.featureMediaId) {
      hasChanges = true
      // Convert to number or null for GraphQL mutation
      changes.featureMediaId = mediaValues.featureMediaId
        ? Number(mediaValues.featureMediaId)
        : null
    }

    return hasChanges
      ? ({
          contentInput: changes,
          generatePath: false, // Media changes don't affect path generation
        } as ContentInfoChanges)
      : null
  }, [id, mediaValues, initialValues])

  // Notify parent when changed values update
  useEffect(() => {
    onChangesUpdate(changedValues)
  }, [changedValues, onChangesUpdate])

  // Handle reset triggered from parent
  useEffect(() => {
    if (onResetTriggered) {
      setMediaValues({ ...initialValues })
    }
  }, [onResetTriggered, initialValues])

  // Handle media field changes from VideoContentMediaGraphQL
  const handleMediaChange = (event: { target: { name: string; value: string | null } }) => {
    const { name, value } = event.target
    setMediaValues((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  return <ContentMedia onChange={handleMediaChange} disabled={isViewOnly} values={mediaValues} />
}

export default ContentMediaSection
